import { useState, useCallback, useEffect, useRef } from 'react';
import './MatrixRenderer.css';

import {
    SimpleCheckbox,
    SimpleRadio,
    SimpleSwitch,
    SimpleButton,
    TTSButton,
    ErrorMessage,
    Loading,
    StatusMessage,
} from '@/src/ui';
import type { QtiMatrixQuestionDTO } from '../../models';
import {
    DynamicEquationEditor,
    DynamicTextPreview,
    type QuestionRendererOptions,
    defaultOptions,
} from '@/src/lib';
import { Constants } from '@/src/utils';
import { submitForGrading } from '../../services';
const dte_default = Constants.DTE_DEFAULT_TEXT;

export type CellValues = {
    rowId: string;
    columnId: string;
    value: string;
};

interface MatrixRendererProps {
    question: QtiMatrixQuestionDTO;
    loadedUserResponse?: string;
    handleUpdateUserResponse?: (response: string, didUserInteract: boolean) => void;
    options?: QuestionRendererOptions;
}

export default function MatrixRenderer({
    question,
    loadedUserResponse,
    handleUpdateUserResponse,
    options = defaultOptions,
}: MatrixRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showLanguageToggle = false,
        showAudioButtons = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [error, setError] = useState<string | null>(null);
    const [userAnswers, setUserAnswers] = useState<CellValues[]>([]);

    // Language may need to be hanldled internally if it is not controlled from the outside
    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);
    useEffect(() => {
        setInternalLanguage(language);
    }, [language]);

    // Use if scoring needs to be handled internally
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    // Manage save data
    const userInteractedRef = useRef(false);
    useEffect(() => {
        if (loadedUserResponse) {
            const parsed = JSON.parse(loadedUserResponse);
            if (parsed.cellAnswers) {
                setUserAnswers(parsed.cellAnswers);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        handleUpdateUserResponse(
            JSON.stringify({ cellAnswers: userAnswers }),
            userInteractedRef.current
        );
        userInteractedRef.current = false;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAnswers]);

    useEffect(() => {
        if (loadedUserResponse) {
            try {
                const parsed = JSON.parse(loadedUserResponse);
                if (parsed.cellValues) {
                    setUserAnswers(parsed.cellValues);
                }
            } catch (e) {
                console.error('Invalid loadedUserResponse JSON:', e);
            }
        } else {
            const nRows = question.rows.length;
            const nCols = question.columns.length;
            const initialAnswers: CellValues[] = [];
            for (let i = 0; i < nRows; i++) {
                const rowId = question.rows[i].id;
                for (let j = 0; j < nCols; j++) {
                    const columnId = question.columns[j].id;
                    let value = 'false';
                    const cell = question.cells.find(
                        (c) => c.rowId === rowId && c.columnId === columnId
                    );
                    if (cell?.type === 'staticText') {
                        continue;
                    } else if (cell?.type === 'openText') {
                        value = dte_default;
                    } else if (cell?.type === 'radio' || cell?.type === 'check') {
                        value = 'false';
                    }
                    initialAnswers.push({ rowId, columnId, value });
                }
            }

            setUserAnswers(initialAnswers);
        }
    }, [loadedUserResponse, question.cells, question.columns, question.rows]);

    const handleSetAnswer = (rowId: string, columnId: string, value: string) => {
        const newUserAnswers = [...userAnswers];

        const cellIndex = userAnswers.findIndex(
            (c) => c.rowId === rowId && c.columnId === columnId
        );

        const systemCell = question.cells.find((c) => c.rowId === rowId && c.columnId === columnId);
        if (cellIndex === -1 || !systemCell) return;
        if (systemCell.type === 'radio') {
            userAnswers.forEach((cell, index) => {
                if (cell.rowId !== rowId || cell.columnId === columnId) {
                    return;
                }

                const cellType = question.cells.find(
                    (c) => c.rowId === cell.rowId && c.columnId === cell.columnId
                )?.type;
                if (cellType === 'radio') {
                    newUserAnswers[index].value = 'false';
                }
            });
        }

        newUserAnswers[cellIndex].value = value;
        userInteractedRef.current = true;
        setUserAnswers(newUserAnswers);
    };

    const uuid = question.uuid || 'matrix-preview-uuid';

    return (
        <>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}
            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}
            <div className="overflow-x-auto matrix-renderer">
                <table className="table-auto w-full border border-collapse">
                    {!question?.additionalMeta.excludeHeaderRow && (
                        <thead>
                            <tr>
                                {question.additionalMeta.excludeHeaderColumn ? null : (
                                    <th className="border px-2 py-1">&nbsp;</th>
                                )}
                                {question.columns.map((col) => (
                                    <th
                                        key={`${uuid}-${col.id}`}
                                        className="border px-2 py-1 max-w-[200px] break-words"
                                        onContextMenu={(e) => {
                                            if (options.onRendererContextMenu) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                options.onRendererContextMenu(
                                                    e,
                                                    {
                                                        language: internalLanguage,
                                                        text: col.text,
                                                        text_es: col.text_es,
                                                    },
                                                    []
                                                );
                                            }
                                        }}
                                    >
                                        <div className="min-w-[100px]">
                                            <DynamicTextPreview
                                                content={
                                                    internalLanguage === 'en'
                                                        ? col.text
                                                        : col.text_es
                                                }
                                            />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {question.rows.map((row, rowIndex) => {
                            return (
                                <tr key={row.id}>
                                    {!question.additionalMeta.excludeHeaderColumn && (
                                        <td
                                            className="border px-2 py-1 max-w-[250px] break-words"
                                            onContextMenu={(e) => {
                                                if (options.onRendererContextMenu) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    options.onRendererContextMenu(
                                                        e,
                                                        {
                                                            language: internalLanguage,
                                                            text: row.text,
                                                            text_es: row.text_es,
                                                        },
                                                        []
                                                    );
                                                }
                                            }}
                                        >
                                            <div className="min-w-[100px]">
                                                <DynamicTextPreview
                                                    content={
                                                        internalLanguage === 'en'
                                                            ? row.text
                                                            : row.text_es
                                                    }
                                                />
                                            </div>
                                        </td>
                                    )}
                                    {question.columns.map((col, colIndex) => {
                                        const key = `${uuid}-${row.id}-${col.id}`;
                                        const cellIndex = question.cells.findIndex(
                                            (c) => c.rowId === row.id && c.columnId === col.id
                                        );
                                        const cell = question.cells[cellIndex];

                                        const answerCellIndex = userAnswers.findIndex(
                                            (c) => c.rowId === row.id && c.columnId === col.id
                                        );
                                        const answerCell = userAnswers[answerCellIndex];
                                        if (!cell || (!answerCell && cell.type !== 'staticText')) {
                                            return (
                                                <td
                                                    key={key}
                                                    className="border px-2 py-1 text-red-600"
                                                >
                                                    N/A
                                                </td>
                                            );
                                        }
                                        if (cell.type === 'staticText') {
                                            return (
                                                <td
                                                    key={key}
                                                    className="border px-2 py-1 max-w-[200px] break-words"
                                                    onContextMenu={(e) => {
                                                        if (options.onRendererContextMenu) {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            options.onRendererContextMenu(
                                                                e,
                                                                {
                                                                    language: internalLanguage,
                                                                    text: cell.staticContent || '',
                                                                    text_es:
                                                                        cell.staticContent_es || '',
                                                                },
                                                                []
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <div className="min-w-[100px]">
                                                        <DynamicTextPreview
                                                            content={
                                                                (internalLanguage === 'en'
                                                                    ? cell.staticContent
                                                                    : cell.staticContent_es) ||
                                                                dte_default
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            );
                                        }
                                        if (cell.type === 'openText') {
                                            return (
                                                <td key={key} className="border px-2 py-1">
                                                    <div className="px-2 py-1 max-w-[225px] break-words">
                                                        <DynamicEquationEditor
                                                            contentKey={`${row.id}-${col.id}`}
                                                            initialValue={
                                                                answerCell.value || dte_default
                                                            }
                                                            setOutput={(val) =>
                                                                handleSetAnswer(row.id, col.id, val)
                                                            }
                                                            disabled={disabled}
                                                            style={{ minWidth: '200px' }}
                                                            showKeypad={
                                                                question.meta.shouldShowKeypad
                                                            }
                                                            allowedKeys={
                                                                question.meta.keypadSetting
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            );
                                        }
                                        if (cell.type === 'radio') {
                                            return (
                                                <td
                                                    key={key}
                                                    className="border px-2 py-1 text-center"
                                                >
                                                    <SimpleRadio
                                                        name={`radio-${rowIndex}`}
                                                        value={`radio-${rowIndex}-${colIndex}`}
                                                        checked={answerCell.value === 'true'}
                                                        onChange={(e) =>
                                                            handleSetAnswer(
                                                                row.id,
                                                                col.id,
                                                                e.target.checked ? 'true' : 'false'
                                                            )
                                                        }
                                                        disabled={disabled}
                                                    />
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={key} className="border px-2 py-1 text-center">
                                                <SimpleCheckbox
                                                    name={`checkbox-${rowIndex}-${colIndex}`}
                                                    checked={answerCell.value === 'true'}
                                                    onChange={(e) =>
                                                        handleSetAnswer(
                                                            row.id,
                                                            col.id,
                                                            e.target.checked ? 'true' : 'false'
                                                        )
                                                    }
                                                    disabled={disabled}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {showScoring && (
                <MatrixScoring
                    question={question}
                    userResponse={userResponse}
                    language={internalLanguage}
                />
            )}
            <ErrorMessage error={error} />
        </>
    );
}

interface MatrixScoringProps {
    question: QtiMatrixQuestionDTO;
    userResponse: string;
    language: 'en' | 'es';
}

function MatrixScoring({ question, userResponse }: MatrixScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    useEffect(() => {
        setAnswerChecked(false);
    }, [userResponse]);

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    const parsedResponse = userResponse ? JSON.parse(userResponse) : null;
    if (question.meta.isHandScored === true || !parsedResponse) return null;

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                <Loading visible={loading} message="Checking answer..." />
            </div>

            <ErrorMessage error={error} />
            <StatusMessage
                color={isCorrect ? 'green' : 'red'}
                visible={answerChecked}
                status={isCorrect ? 'Correct!' : `Incorrect`}
            />
        </div>
    );
}
